/**
 * Print-only numbered list row atom for Monthly Consultation PDF.
 */

import React from "react";
import {
  bodyFontFamily,
  border,
  charcoal,
  deepRed,
  mutedGray,
} from "../pdfTheme";

/**
 * Props for a numbered list row with optional supporting body text.
 */
export interface PdfListItemProps {
  /** 1-based display index shown in the marker. */
  index: number;
  /** Primary row title. */
  title: string;
  /** Optional secondary supporting text under the title. */
  body?: string;
}

/**
 * Numbered list row with a circular index marker and optional sub-text.
 * Used for signature behaviors, tips, and action steps.
 */
export const PdfListItem: React.FC<PdfListItemProps> = ({
  index,
  title,
  body,
}) => {
  const safeTitle = typeof title === "string" ? title.trim() : "";
  if (safeTitle.length === 0) {
    return null;
  }

  const safeIndex =
    typeof index === "number" && Number.isFinite(index) && index > 0
      ? Math.floor(index)
      : 0;
  const safeBody =
    typeof body === "string" && body.trim().length > 0 ? body.trim() : null;

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        width: "100%",
        boxSizing: "border-box",
        fontFamily: bodyFontFamily,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: `1px solid ${border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          color: deepRed,
          backgroundColor: "transparent",
          lineHeight: 1,
          marginTop: 1,
        }}
      >
        {safeIndex > 0 ? String(safeIndex) : "·"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: charcoal,
            lineHeight: 1.4,
          }}
        >
          {safeTitle}
        </div>
        {safeBody !== null ? (
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 12,
              lineHeight: 1.5,
              color: mutedGray,
            }}
          >
            {safeBody}
          </p>
        ) : null}
      </div>
    </div>
  );
};
